/**
 * Authentication service (online-only version)
 * This is a simplified version that maintains API compatibility
 * but does not store or retrieve data from offline storage
 */

/**
 * Simplified placeholder for saving authentication data
 * In online-only mode, we don't save auth data locally
 * @param {Object} authData - Authentication data including user and token
 */
const saveAuthData = async (authData) => {
  if (!authData) return;
  
  try {
    const userId = authData.user?.id || 'anonymous';
    console.log(`[Online Mode] Auth data would have been saved for user ${userId}`);
    // In online-only mode, we don't store auth data locally
    return true;
  } catch (error) {
    console.error('Error in saveAuthData:', error);
    return false;
  }
};

/**
 * Get cached authentication data
 * In online-only mode, we don't retrieve cached auth data
 * @returns {Promise<Object>} - Always returns null in online-only mode
 */
const getAuthData = async () => {
  try {
    console.log('[Online Mode] No cached auth data is available in online-only mode');
    // In online-only mode, we don't retrieve cached auth data
    return null;
  } catch (error) {
    console.error('Error in getAuthData:', error);
    return null;
  }
};

/**
 * Clear cached authentication data
 * In online-only mode, this is a no-op
 */
const clearAuthData = async () => {
  try {
    console.log('[Online Mode] No cached auth data to clear in online-only mode');
    // In online-only mode, there's no cached auth data to clear
    return true;
  } catch (error) {
    console.error('Error in clearAuthData:', error);
    return false;
  }
};

/**
 * Check if there's a specific user's data stored
 * In online-only mode, we never have cached user data
 * @param {string} userId - The user ID to check for
 * @returns {Promise<boolean>} - Always returns false in online-only mode
 */
const hasUserData = async (userId) => {
  try {
    if (!userId) return false;
    
    console.log(`[Online Mode] No offline data exists for any user (${userId})`);
    // In online-only mode, we don't have cached user data
    return false;
  } catch (error) {
    console.error(`Error in hasUserData for user ${userId}:`, error);
    return false;
  }
};

/**
 * Switch to a different user's data
 * In online-only mode, this is a no-op
 * @param {string} userId - ID of the user to switch to
 * @returns {Promise<boolean>} - Always returns false in online-only mode
 */
const switchUser = async (userId) => {
  try {
    if (!userId) return false;
    
    console.log(`[Online Mode] Cannot switch to user ${userId}'s offline data in online-only mode`);
    // In online-only mode, we can't switch to offline user data
    return false;
  } catch (error) {
    console.error(`Error in switchUser for user ${userId}:`, error);
    return false;
  }
};

const authOffline = {
  saveAuthData,
  getAuthData,
  clearAuthData,
  hasUserData,
  switchUser
};

export default authOffline;
